import React, { useCallback, useEffect, useState } from "react";
import {
  listStandardCourseAhaProducts, setAhaProductEnvironmentStatus, unlinkStandardCourseAhaProduct,
} from "../../../../api/apiClient";
import { ConfirmDialog, EnvBadge, Notice, StatePanel } from "../../ui/AdminUi";
import { I } from "../../ui/icons";
import { errorText } from "../common";
import AhaProductPicker from "./AhaProductPicker";
import { ENV_LABEL, ENV_ORDER, STATE, cellActions, consoleEnvOf, productState } from "./model";
import "./integrations.css";

/** Active here, or in an environment this backend is not connected to: either way unlinking turns eCards off somewhere. */
const isLive = (product) => Boolean(product.active || product.activeAnywhere);

const ICONS = { check: I.check, help: I.help, shield: I.shield, minus: I.minus };

function StateChip({ state }) {
  const s = STATE[state];
  const Icon = ICONS[s.icon];
  return <span className={`chip ${s.tone}`}><Icon s={12} />{s.label}</span>;
}

/** Status of one product in one environment, with the action it offers. Only the connected environment can change. */
function EnvCell({ product, env, connected, retired, publishedHere, busy, onAct }) {
  if (!connected) {
    return <div className="ecell"><span className="chip unk"><I.minus s={12} />Not visible here</span><span className="hint">{cellActions("none", { connected: false }).hint}</span></div>;
  }
  const state = productState(product);
  const actions = cellActions(state, { connected, retired, publishedHere });
  return (
    <div className="ecell">
      <StateChip state={state} />
      {!retired && (
        <div className="ecell-a">
          {actions.verify && <button type="button" className="b sec sm" disabled={busy} onClick={() => onAct(product, env, "verify")}><I.shield s={12} />Verify</button>}
          {actions.activate && (
            <button type="button" className={`b sm ${env === "production" ? "pri" : "sec"}`} disabled={busy} onClick={() => onAct(product, env, "activate")}>
              {env === "production" && <I.lock s={11} />}Activate
            </button>
          )}
          {actions.deactivate && <button type="button" className="b dsec sm" disabled={busy} onClick={() => onAct(product, env, "deactivate")}>Deactivate</button>}
          {actions.hint && <span className="hint">{actions.hint}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * The AHA connector of one standard course: which AHA products it certifies and where each is live. Publishing a version
 * freezes the content; activating turns the AHA link on in one environment. They are separate steps on purpose.
 */
export default function IntegrationsTab({ courseId, course, onChanged }) {
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [picking, setPicking] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [unlinking, setUnlinking] = useState(null);
  const retired = course?.status === "RETIRED";

  const load = useCallback(() => listStandardCourseAhaProducts(courseId)
    .then((list) => { setProducts(Array.isArray(list) ? list : []); setError(null); })
    .catch((e) => { setProducts([]); setError(errorText(e, "The linked products could not be loaded.")); }), [courseId]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); if (onChanged) onChanged(); };

  const connectedEnv = consoleEnvOf(products?.[0]?.environment);
  const publishedHere = Boolean(course?.currentVersionNumber);

  const setStatus = async (product, flags, done) => {
    setBusyKey(product.key);
    setError(null);
    try {
      await setAhaProductEnvironmentStatus(product.key, product.environment, flags);
      setNotice(done);
      changed();
    } catch (e) {
      setError(errorText(e, `${product.name} could not be updated.`));
    } finally {
      setBusyKey(null);
    }
  };

  const onAct = (product, env, action) => {
    if (action === "verify") setStatus(product, { verified: true }, `${product.name} verified in ${ENV_LABEL[env]}.`);
    else setConfirm({ product, env, action });
  };

  const confirmAct = () => {
    const { product, env, action } = confirm;
    setConfirm(null);
    setStatus(product, { active: action === "activate" }, `${product.name} ${action === "activate" ? "activated" : "deactivated"} in ${ENV_LABEL[env]}.`);
  };

  const unlink = async () => {
    const product = unlinking;
    setUnlinking(null);
    setError(null);
    try {
      await unlinkStandardCourseAhaProduct(courseId, product.key, isLive(product));
      setNotice(`${product.name} unlinked. It stays in the Catalog and can be linked again.`);
      changed();
    } catch (e) {
      setError(errorText(e, `${product.name} could not be unlinked.`));
    }
  };

  return (
    <div data-testid="integrations-tab" style={{ display: "grid", gap: 16 }}>
      {retired
        ? <Notice kind="neutral">Retired course. Integrations are read-only. Linked products stay in the Catalog and can be linked to another course there.</Notice>
        : (
          <div className="panel sc-two">
            <div><span className="n">1</span><b>Publish a version</b><span>Freezes content so centers can pull it. Done on Review and publish.</span></div>
            <div><span className="n">2</span><b>Activate in an environment</b><span>Turns on the AHA link for one environment. Done here, after publishing.</span></div>
          </div>
        )}
      {notice && <Notice kind="ok">{notice} <button type="button" className="b ghost sm" onClick={() => setNotice(null)}>Dismiss</button></Notice>}
      {error && products?.length > 0 && <Notice kind="bad">{error} <button type="button" className="b ghost sm" onClick={() => setError(null)}>Dismiss</button></Notice>}

      <div className="panel">
        <div className="panel-h">
          <div style={{ minWidth: 0 }}>
            <h3>AHA eCards</h3>
            <div className="sub" style={{ fontSize: 12.5, marginTop: 2 }}>The AHA products this course certifies and where each is live. The product a class issues is chosen on the Details tab.</div>
          </div>
          <span className="sp" />
          {!retired && products?.length > 0 && <button type="button" className="b sec sm" onClick={() => setPicking(true)}><I.plus s={12} />Link product</button>}
        </div>

        {products === null ? <StatePanel kind="loading" what="linked products" />
          : error && products.length === 0 ? <StatePanel kind="error" what="linked products" error={error} onRetry={load} />
            : products.length === 0 ? (
              <div className="state" style={{ padding: "32px 20px" }}>
                <span className="ic"><I.link s={20} /></span>
                <b>No AHA products linked</b>
                <p>Link the AHA product this course certifies so classes built from it can issue eCards.</p>
                {!retired && <button type="button" className="b pri" onClick={() => setPicking(true)}><I.link s={14} />Link product</button>}
              </div>
            ) : (
              <div className="tw">
                <table className="tbl stack envg">
                  <thead>
                    <tr>
                      <th>{products.length} linked product{products.length > 1 ? "s" : ""}</th>
                      {ENV_ORDER.map((env) => (
                        <th key={env} className={`eh${env === "production" ? " prod" : ""}`}>{env === "production" && <I.lock s={11} />} {ENV_LABEL[env]}{env === connectedEnv ? " · acting here" : ""}</th>
                      ))}
                      {!retired && <th style={{ width: 44 }}><span className="sr">Unlink</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="vrow">
                      <td className="lead"><span className="eyebrow">Course version deployed</span></td>
                      {ENV_ORDER.map((env) => (
                        <td key={env} data-label={ENV_LABEL[env]}>
                          {env !== connectedEnv ? <span className="sub" style={{ fontSize: 13 }}>—</span>
                            : publishedHere ? <span className="vtag"><b className="mono">v{course.currentVersionNumber}</b><span className="sub">published</span></span>
                              : <span className="sub" style={{ fontSize: 13 }}>Not published</span>}
                        </td>
                      ))}
                      {!retired && <td />}
                    </tr>
                    {products.map((p) => (
                      <tr key={p.key} data-testid={`linked-${p.key}`}>
                        <td className="lead">
                          <b style={{ fontWeight: 600 }}>{p.name}{p.edition ? ` (${p.edition})` : ""}</b>
                          <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                            {p.productNumber && <span className="code">{p.productNumber}</span>}
                            <span className="mono sub" style={{ fontSize: 11.5 }}>{p.key}</span>
                          </div>
                        </td>
                        {ENV_ORDER.map((env) => (
                          <td key={env} data-label={ENV_LABEL[env]} className="ec">
                            <EnvCell product={p} env={env} connected={env === connectedEnv} retired={retired} publishedHere={publishedHere}
                              busy={busyKey === p.key} onAct={onAct} />
                          </td>
                        ))}
                        {!retired && (
                          <td className="r">
                            <button type="button" className="ib" aria-label={`Unlink ${p.name}`} title="Unlink" onClick={() => setUnlinking(p)}><I.x s={15} /></button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        {products?.length > 0 && !retired && (
          <div className="pfoot"><I.info s={13} />Activate is available once a version is published. This backend can only change the environment it is connected to; the other columns show where to switch.</div>
        )}
      </div>

      {picking && (
        <AhaProductPicker courseId={courseId} courseTitle={course?.title || "this course"} onClose={() => setPicking(false)}
          onLinked={(n) => { if (n) { setNotice(`${n} product${n > 1 ? "s" : ""} linked.`); changed(); } }} />
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        env={confirm?.env}
        destructive={confirm?.action === "deactivate"}
        title={confirm ? `${confirm.action === "activate" ? "Activate" : "Deactivate"} ${confirm.product.name} in ${ENV_LABEL[confirm.env]}?` : "Confirm"}
        confirmLabel={confirm ? `${confirm.action === "activate" ? "Activate" : "Deactivate"} in ${ENV_LABEL[confirm.env]}` : "Confirm"}
        typed={confirm?.env === "production" ? confirm.product.productNumber : undefined}
        consequence={confirm ? (confirm.action === "activate"
          ? `Turns on the AHA link for ${course?.title || "this course"} in ${ENV_LABEL[confirm.env]}. Classes built from v${course?.currentVersionNumber} can issue ${confirm.product.name} eCards from now on.`
          : `Stops ${confirm.product.name} eCards for ${course?.title || "this course"} in ${ENV_LABEL[confirm.env]}. ${course?.classCount ?? 0} classes using this course can no longer issue them.`) : ""}
        keeps={confirm?.action === "activate" ? "Course content does not change. The published version stays frozen; publishing and activating are separate steps." : "eCards already issued stay valid."}
        onClose={() => setConfirm(null)}
        onConfirm={confirmAct}
      >
        {confirm && <p className="sub" style={{ fontSize: 12.5, margin: 0 }}><EnvBadge env={confirm.env} /> · {confirm.product.key}</p>}
      </ConfirmDialog>

      <ConfirmDialog
        open={Boolean(unlinking)}
        env={connectedEnv}
        title={unlinking ? `Unlink ${unlinking.name}?` : "Unlink?"}
        confirmLabel="Unlink product"
        typed={unlinking && isLive(unlinking) ? (unlinking.productNumber || unlinking.key) : undefined}
        consequence={unlinking ? (isLive(unlinking)
          ? `${unlinking.name} is active in ${unlinking.active ? ENV_LABEL[connectedEnv] : "an AHA environment"}. Unlinking stops classes built from ${course?.title || "this course"} from issuing it.`
          : `${unlinking.name} is not active in any AHA environment. Nothing changes for training centers.`) : ""}
        keeps="The product stays in the Catalog with its verification status, and can be linked again later."
        onClose={() => setUnlinking(null)}
        onConfirm={unlink}
      />
    </div>
  );
}
